<template>
	<div
		class="media-grid-container"
		:class="{ [`items-${mediaItems.length}`]: true }"
		v-if="mediaItems.length > 0"
		@mouseleave="onMouseLeave"
	>
		<div
			class="media-grid-item"
			v-for="media in mediaItems"
			:key="media.key"
			:class="media.class"
			:style="media.style"
			@click="emit('itemClick', media)"
		>
			<img
				class="media-grid-thumbnail ph-no-capture"
				v-if="media.isVisual && media.src"
				:src="media.src"
				@load="onLoadSuccess($event, media)"
				@error="onLoadError($event, media)"
			/>
			<div class="video-overlay" v-if="media.showVideo">
				<PlayIcon type="solid" />
			</div>
			<div class="hover-overlay" />
			<div class="more-overlay" v-if="media.isLastOfMore">+{{ moreCount }}</div>
			<div v-if="media.showGif" class="gif-icon">
				<GifTextIcon />
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, watch, nextTick, reactive, ref, inject } from 'vue'
import { PlayIcon, GifTextIcon } from '@/icons'
import { generateGifThumbnailFromUrl } from '@/helpers/utils'
import { getBlob } from '@/store/uploads'
import { IMAGE_MAX_WIDTH } from '@/helpers/constants'
import { createLogger } from '@/helpers/log'

const MAX_VISIBLE = 5

const props = defineProps({
	message: Object,
	autoPlayGif: { type: Boolean, default: false },
})
const log = createLogger('MediaGrid', true)
const emit = defineEmits(['itemClick'])
const blobsByUUID = reactive({})
const autoplay = ref(props.autoPlayGif)
const loaded = reactive({})
const loadErrors = reactive({})
const MAX_SIZE = inject('maxMediaSize') || IMAGE_MAX_WIDTH

const visualItems = computed(
	() => props.message?.messagemedia?.filter((m) => m.isVisual) || []
)
const mediaItems = computed(() => {
	if (!visualItems.value?.length) return []
	const isMore = visualItems.value.length > MAX_VISIBLE
	return visualItems.value.slice(0, MAX_VISIBLE).map((m, i) => {
		m = Object.assign({}, m)
		const blob = getBlob(m.tempId || m.uuid)
		m.key = m.uuid || m.id
		m.src = m.thumbnailUrl || m.mediaUrl
		// if the message is in a 'sending' state, then the media items might not be uploaded
		// yet and therefore we have to use the local blob urls to display the images/thumbs
		// but we want to display the remote items once they are available so we render the
		// blob photo as a background-image so the remote <img> can render in front of it
		let styleUrl = blob || null
		if (m.isGif) {
			styleUrl = m.thumbnailUrl || blobsByUUID[m.uuid] || m.mediaUrl
			m.src = m.mediaUrl
		}
		m.style = styleUrl ? { 'background-image': `url(${styleUrl})` } : null
		m.isLastOfMore = isMore && i === MAX_VISIBLE - 1
		m.class = [
			`item-${i + 1}`,
			m.isGif && 'gif',
			autoplay.value && !m.isLastOfMore && m.isGif && 'autoplay',
			!loaded[m.key] && !loadErrors[m.key] && 'loading',
			loadErrors[m.key] && 'load-error',
		]
			.filter((x) => x)
			.join(' ')
		m.showVideo = m.isVideo && !m.isLastOfMore
		m.showGif = m.isGif && !m.isLastOfMore
		return m
	})
})
const moreCount = computed(
	() => visualItems.value.length - mediaItems.value.length
)

function onLoadSuccess(e, media) {
	loaded[media.key] = true
}

function onLoadError(e, media) {
	log('media failed to load', media, e)
	loadErrors[media.key] = true
}

function onMouseLeave() {
	if (autoplay.value) {
		autoplay.value = false
	}
}

nextTick(() => {
	// thumbnail generation can be resource intensive, so we want to offload that until
	// after the remainder of the grid has rendered
	watch(
		mediaItems,
		() => {
			// if gifs get added, we need to generate a "paused" thumbnail from the file
			mediaItems.value
				.filter((m) => m.isGif && !m.thumbnailUrl && !blobsByUUID[m.uuid])
				.forEach((m) => {
					generateGifThumbnailFromUrl(m.mediaUrl)
						.then((blobUrl) => {
							blobsByUUID[m.uuid] = blobUrl
						})
						.catch((e) => console.error(e))
				})
		},
		{ immediate: true }
	)
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.media-grid-container {
	display: grid;
	width: calc(v-bind(MAX_SIZE) * 1px); // convert integer into pixels
	grid-template-columns: repeat(6, 1fr);
	grid-template-rows: repeat(10, 1fr);
	grid-gap: 2px;
	overflow: hidden;
	border-radius: 6px;
	aspect-ratio: 1.22;

	--small-icon: 26px;
	--med-icon: 30px;
	--large-icon: 36px;
	--xlarge-icon: 42px;

	> .media-grid-item {
		position: relative;
		background-position: center;
		background-size: cover;
		background-repeat: no-repeat;
		cursor: pointer;
		&:hover {
			.hover-overlay {
				display: block;
				opacity: 0.3;
				z-index: 3;
			}
		}
		&.loading {
			background-color: var(--neutral-200);
		}
		&.gif {
			&:hover,
			&.autoplay {
				.media-grid-thumbnail {
					opacity: 1;
				}
				.gif-icon {
					opacity: 0;
				}
			}
			.media-grid-thumbnail {
				opacity: 0;
			}
			// .hover-overlay {
			// 	display: none !important;
			// }
		}
		&.load-error {
			background-color: var(--neutral-200) !important;
			background-image: url('/images/common/broken-image.svg') !important;
			background-size: 30% !important;
			background-position: center;
			background-repeat: no-repeat;
			.media-grid-thumbnail {
				opacity: 0;
			}
		}
		.video-overlay,
		.more-overlay,
		.hover-overlay {
			@include absolute-cover(1);
			@include flex;
			justify-content: center;
			background-color: rgba(0, 0, 0, 0.4);
			color: white;
		}
		.more-overlay {
			font-size: 24px;
			@include fontFamily(800);
		}
		.hover-overlay {
			display: none;
			opacity: 0;
			z-index: 0;
			transition: opacity ease-in-out 150ms;
		}
		.media-grid-thumbnail {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
		.gif-icon {
			position: absolute;
			top: 0;
			left: 0;
			@include flex;
			justify-content: center;
			background: rgba(0, 0, 0, 0.65);
			width: 36px;
			height: 29px;
			transition: opacity ease-in-out 150ms;
		}
	}

	&.items-1 {
		.item-1 {
			grid-column: 1 / span 6;
			grid-row: 1 / span 10;
			.pronto-icon {
				// video play icon needs to scale with thumbnail size
				font-size: var(--xlarge-icon);
			}
		}
	}

	&.items-2 {
		.item-1 {
			grid-column: 1 / span 3;
			grid-row: 1 / span 10;
		}
		.item-2 {
			grid-column: 4 / span 3;
			grid-row: 1 / span 10;
		}
		.pronto-icon {
			font-size: var(--large-icon);
		}
	}

	&.items-3 {
		.item-1 {
			grid-column: 1 / span 3;
			grid-row: 1 / span 10;
			.pronto-icon {
				font-size: var(--large-icon);
			}
		}
		.item-2 {
			grid-column: 4 / span 3;
			grid-row: 1 / span 5;
			.pronto-icon {
				font-size: var(--med-icon);
			}
		}
		.item-3 {
			grid-column: 4 / span 3;
			grid-row: 6 / span 5;

			.pronto-icon {
				font-size: var(--med-icon);
			}
		}
	}

	&.items-4 {
		.item-1 {
			grid-column: 1 / span 3;
			grid-row: 1 / span 5;
		}
		.item-2 {
			grid-column: 4 / span 3;
			grid-row: 1 / span 5;
		}
		.item-3 {
			grid-column: 1 / span 3;
			grid-row: 6 / span 5;
		}
		.item-4 {
			grid-column: 4 / span 3;
			grid-row: 6 / span 5;
		}
		.pronto-icon {
			font-size: var(--med-icon);
		}
	}

	&.items-5 {
		.item-1 {
			grid-column: 1 / span 3;
			grid-row: 1 / span 6;
			.pronto-icon {
				font-size: var(--med-icon);
			}
		}
		.item-2 {
			grid-column: 4 / span 3;
			grid-row: 1 / span 6;
			.pronto-icon {
				font-size: var(--med-icon);
			}
		}
		.item-3 {
			grid-column: 1 / span 2;
			grid-row: 7 / span 4;
			.pronto-icon {
				font-size: var(--small-icon);
			}
		}
		.item-4 {
			grid-column: 3 / span 2;
			grid-row: 7 / span 4;
			.pronto-icon {
				font-size: var(--small-icon);
			}
		}
		.item-5 {
			grid-column: 5 / span 2;
			grid-row: 7 / span 4;
			.pronto-icon {
				font-size: var(--small-icon);
			}
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.media-grid-container {
		.media-grid-item {
			// https://css-tricks.com/a-complete-guide-to-dark-mode-on-the-web/#aa-dark-mode-images
			filter: brightness(0.8) contrast(1.2);
		}
	}
}
</style>
